import { Link, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ChevronDown, Menu, Search } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrandLogo } from "@/components/brand-logo";
import { Button } from "@/components/ui/button";
import { getAuthStatus, signOutProvider } from "@/lib/auth.functions";
import type { AuthStatus } from "@/lib/auth.types";
import { languages, useLanguage, type LanguageCode } from "@/lib/i18n";

const HeaderAccountMenu = lazy(() => import("@/components/header-account-menu"));
const HeaderMobileMenu = lazy(() => import("@/components/header-mobile-menu"));
const JobNotificationsBell = lazy(() =>
  import("@/components/jobs/job-notifications").then((module) => ({
    default: module.JobNotificationsBell,
  })),
);

function Logo() {
  return (
    <Link
      to="/"
      aria-label="Begner Startseite"
      className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <BrandLogo />
    </Link>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const locationHref = useRouterState({ select: (state) => state.location.href });
  const loadAuthStatus = useServerFn(getAuthStatus);
  const logout = useServerFn(signOutProvider);
  const { language, setLanguage, t } = useLanguage();
  const accountReady = authStatus?.authenticated
    ? authStatus.profile?.role === "kunde" || authStatus.profile?.providerStatus === "approved"
    : false;

  useEffect(() => {
    let active = true;
    loadAuthStatus()
      .then((status) => {
        if (active) setAuthStatus(status);
      })
      .catch(() => {
        if (active) setAuthStatus({ authenticated: false, configured: false });
      });
    return () => {
      active = false;
    };
  }, [loadAuthStatus, locationHref]);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      const result = await logout();
      if (!result.ok) return;
      setAuthStatus({ authenticated: false, configured: true });
      window.location.assign("/");
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="container-page flex h-15 items-center justify-between gap-3 sm:h-16 sm:gap-6">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex">
          {t.header.navItems.map((item) => (
            <Link
              key={`${item.to}:${item.hash ?? ""}`}
              to={item.to}
              hash={item.hash}
              activeOptions={{ exact: true, includeHash: true }}
              className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              activeProps={{ className: "bg-brand-soft text-brand-deep shadow-sm" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {authStatus?.authenticated && authStatus.user.emailConfirmed ? (
            <Suspense fallback={null}>
              <JobNotificationsBell />
            </Suspense>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            aria-label={t.header.searchLabel}
            asChild
          >
            <Link to="/auftragnehmer">
              <Search className="h-4 w-4" />
            </Link>
          </Button>
          <div className="group relative hidden lg:block">
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 text-xs font-bold text-brand-deep shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`${language.toUpperCase()} – Sprache auswählen`}
              aria-haspopup="menu"
            >
              {language.toUpperCase()}
              <ChevronDown
                className="size-3.5 text-muted-foreground transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180"
                aria-hidden
              />
            </button>

            <div className="pointer-events-none invisible absolute right-0 top-full z-50 w-40 translate-y-1 pt-2 opacity-0 transition duration-150 group-hover:pointer-events-auto group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
              <div
                className="rounded-xl border border-border bg-card p-1.5 shadow-card"
                role="menu"
                aria-label="Sprache auswählen"
              >
                {languages
                  .filter((item) => item.code !== language)
                  .map((item) => (
                    <button
                      key={item.code}
                      type="button"
                      role="menuitem"
                      onClick={(event) => {
                        setLanguage(item.code as LanguageCode);
                        event.currentTarget.blur();
                      }}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-brand-soft hover:text-brand-deep focus:bg-brand-soft focus:text-brand-deep focus:outline-none"
                    >
                      <span>{item.name}</span>
                      <span className="text-xs font-bold">{item.label}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
          {authStatus?.authenticated ? (
            <Suspense fallback={null}>
              <HeaderAccountMenu
                authStatus={authStatus}
                accountReady={accountReady}
                loggingOut={loggingOut}
                onLogout={() => void handleLogout()}
              />
            </Suspense>
          ) : (
            <Link
              to="/login"
              search={{ mode: "anmelden" }}
              className="hidden text-sm font-medium text-muted-foreground hover:text-foreground lg:inline-flex"
            >
              {t.header.login}
            </Link>
          )}
          <Button asChild className="hidden lg:inline-flex shadow-brand">
            <Link to="/auftrag-einstellen">{t.header.postJob}</Link>
          </Button>

          <Button
            ref={menuTriggerRef}
            type="button"
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={t.header.openMenu}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? "site-mobile-menu" : undefined}
            onClick={() => {
              setMenuLoaded(true);
              setOpen(true);
            }}
          >
            <Menu className="h-5 w-5" />
          </Button>
          {menuLoaded ? (
            <Suspense fallback={null}>
              <HeaderMobileMenu
                open={open}
                setOpen={setOpen}
                authStatus={authStatus}
                loggingOut={loggingOut}
                onLogout={() => void handleLogout()}
                onCloseFocus={() => menuTriggerRef.current?.focus()}
              />
            </Suspense>
          ) : null}
        </div>
      </div>
    </header>
  );
}
